import { Trans } from "@lingui/react/macro";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { AiTab } from "@/components/admin/settings/ai-tab";
import { AuthTab } from "@/components/admin/settings/auth-tab";
import { CurrenciesTab } from "@/components/admin/settings/currencies-tab";
import { LanguagesTab } from "@/components/admin/settings/languages-tab";
import { MessagingTab } from "@/components/admin/settings/messaging-tab";
import { StripeIntegrationCard } from "@/components/admin/stripe-integration-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * Admin → Settings.
 *
 * V1.1 P11 (HIGH effort) — full port of the 5 deferred tabs from
 * src/app/admin/settings/page.tsx (legacy NextJS, ~1950 LOC) per D21
 * NO REINVENTION. The integrations tab (Stripe) was already ported V1.0.
 *
 * Tab implementations (each in its own file under
 * apps/admin/src/components/admin/settings/):
 *   - languages-tab.tsx   — active languages, coverage badges, message files.
 *                            Translation-coverage / message-files / generate-
 *                            messages endpoints currently 501 in apps/api;
 *                            UI degrades to inline "V1.1 backend pending"
 *                            banners and skips destructive actions.
 *   - currencies-tab.tsx  — exchange-rates list + manual refresh
 *                            (frankfurter.app). FULL backend.
 *   - auth-tab.tsx        — OTP provider picker (firebase / disabled).
 *                            Per-country OAuth toggles are read-only here —
 *                            owners live on Country editor.
 *   - ai-tab.tsx          — AI provider config: Symptom Router pick, Groq +
 *                            Anthropic key fields, model fields, Test
 *                            Connection. FULL backend.
 *   - messaging-tab.tsx   — global platform-providers (BulkGate / FCM /
 *                            SendGrid / SMSO) with credential dialog +
 *                            per-provider toggle + test (W7 stub: validates
 *                            decryption only).
 *
 * Cutover-blocking 501 stubs (would unblock the most settings UI):
 *   1. /api/admin/settings/translation-coverage
 *   2. /api/admin/settings/message-files
 *   3. /api/admin/settings/generate-messages
 * All three are i18n filesystem reads/writes — TODO(W7) per
 * apps/api/src/routes/admin-settings.ts:8-16.
 */

type SettingsTab = "languages" | "currencies" | "integrations" | "auth" | "ai" | "messaging";

interface SettingsSearch {
  tab?: SettingsTab;
}

const VALID_TABS: SettingsTab[] = [
  "languages",
  "currencies",
  "integrations",
  "auth",
  "ai",
  "messaging",
];

function isValidTab(value: string): value is SettingsTab {
  return (VALID_TABS as string[]).includes(value);
}

export const Route = createFileRoute("/_authenticated/settings")({
  validateSearch: (search: Record<string, unknown>): SettingsSearch => {
    const raw = typeof search.tab === "string" ? search.tab : undefined;
    return { tab: raw && isValidTab(raw) ? raw : undefined };
  },
  component: SettingsPage,
});

function SettingsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const activeTab: SettingsTab = search.tab ?? "languages";

  const switchTab = (tab: SettingsTab) => {
    void navigate({
      search: () => ({ tab }),
      replace: true,
    });
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Platform Settings</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Global configuration — API keys, providers, feature flags</Trans>
        </p>
      </header>

      <Tabs value={activeTab} onValueChange={(v) => switchTab(v as SettingsTab)}>
        <TabsList>
          <TabsTrigger value="languages">
            <Trans>Languages</Trans>
          </TabsTrigger>
          <TabsTrigger value="currencies">
            <Trans>Currencies</Trans>
          </TabsTrigger>
          <TabsTrigger value="integrations">
            <Trans>Integrations</Trans>
          </TabsTrigger>
          <TabsTrigger value="auth">
            <Trans>Auth</Trans>
          </TabsTrigger>
          <TabsTrigger value="ai">
            <Trans>AI / Claude</Trans>
          </TabsTrigger>
          <TabsTrigger value="messaging">
            <Trans>Messaging</Trans>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="languages" className="space-y-4 pt-4">
          <LanguagesTab />
        </TabsContent>

        <TabsContent value="currencies" className="space-y-4 pt-4">
          <CurrenciesTab />
        </TabsContent>

        <TabsContent value="integrations" className="space-y-6 pt-4">
          <StripeIntegrationCard />
        </TabsContent>

        <TabsContent value="auth" className="space-y-4 pt-4">
          <AuthTab />
        </TabsContent>

        <TabsContent value="ai" className="space-y-4 pt-4">
          <AiTab />
        </TabsContent>

        <TabsContent value="messaging" className="space-y-4 pt-4">
          <MessagingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
